// frontend-web/src/pages/Admin/MegaScholarshipList.jsx

import React, { useEffect, useState } from "react";
import api from "../../api";
import { useNavigate } from "react-router-dom";

export default function MegaScholarshipList() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [allExams, setAllExams] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [classRes, subjectRes, examRes] = await Promise.all([
        api.get("/admin/classes"),
        api.get("/admin/subjects"),
        api.get("/admin/mega_scholarship/list")
      ]);

      const classList = classRes.data || [];
      const subjectList = subjectRes.data || [];
      const examList = examRes.data || [];

      setClasses(classList);
      setSubjects(subjectList);
      setAllExams(examList);

      if (classList.length > 0) {
        setSelectedClass(classList[0]);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedClass) {
      setFilteredExams([]);
      return;
    }

    let filtered = allExams.filter(exam => exam.class_id === selectedClass.id);

    if (selectedSubject) {
      filtered = filtered.filter(exam => {
        const ids = Array.isArray(exam.subject_ids) ? exam.subject_ids : [];
        return ids.includes(selectedSubject.id);
      });
    }

    setFilteredExams(filtered);
  }, [selectedClass, selectedSubject, allExams]);

  const filteredSubjects = selectedClass
    ? subjects.filter(s => s.class_id === selectedClass.id)
    : [];

  const deleteExam = async (id) => {
    if (!window.confirm("Are you sure you want to delete this exam?")) return;
    
    try {
      await api.delete(`/admin/mega_scholarship/${id}`);
      setAllExams((prev) => prev.filter((e) => e.id !== id));
      alert("Deleted successfully");
    } catch (err) {
      alert("Delete failed");
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: "center", fontSize: 20 }}>
        Loading Mega Scholarship Exams...
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "Inter, sans-serif" }}>
      <div style={{ padding: "20px 40px", background: "white", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button
            onClick={() => nav("/admin/dashboard")}
            style={{ padding: "10px 16px", background: "#e2e8f0", border: "none", borderRadius: 8, cursor: "pointer" }}
          >
            ← Back to Dashboard
          </button>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>View Mega Scholarship Exams</h1>
        </div>
      </div>

      <div style={{ padding: 40 }}>
        {/* Class Tabs */}
        <div style={{ marginBottom: 30 }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 600 }}>Select Class</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {classes.map(cls => (
              <button
                key={cls.id}
                onClick={() => {
                  setSelectedClass(cls);
                  setSelectedSubject(null);
                }}
                style={{
                  padding: "10px 20px",
                  borderRadius: 30,
                  border: "none",
                  background: selectedClass?.id === cls.id ? "#3b82f6" : "#e2e8f0",
                  color: selectedClass?.id === cls.id ? "white" : "#475569",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "0.2s"
                }}
              >
                {cls.name}
              </button>
            ))}
          </div>
        </div>

        {/* Subject Tabs */}
        {selectedClass && (
          <div style={{ marginBottom: 30 }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 600 }}>Select Subject</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {filteredSubjects.length === 0 ? (
                <p style={{ color: "#94a3b8" }}>No subjects in this class</p>
              ) : (
                filteredSubjects.map(sub => (
                  <button
                    key={sub.id}
                    type="button"
                    onClick={() => setSelectedSubject(selectedSubject?.id === sub.id ? null : sub)}
                    style={{
                      padding: "10px 20px",
                      borderRadius: 30,
                      border: "none",
                      background: selectedSubject?.id === sub.id ? "#10b981" : "#f1f5f9",
                      color: selectedSubject?.id === sub.id ? "white" : "#1e293b",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "0.2s"
                    }}
                  >
                    {sub.name}
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {filteredExams.length === 0 ? (
          <div style={{ textAlign: "center", padding: 80, background: "white", borderRadius: 16 }}>
            <p style={{ fontSize: 18, color: "#64748b" }}>
              {selectedClass ? "No Mega Scholarship Exams found" : "Please select a class"}
            </p>
          </div>
        ) : (
          <div style={{ background: "white", borderRadius: 16, overflow: "hidden", boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ background: "#1e293b" }}>
                <tr>
                  <th style={th}>#</th>
                  <th style={th}>Exam Title</th>
                  <th style={th}>Questions</th>
                  <th style={th}>Duration</th>
                  <th style={th}>Class</th>
                  <th style={th}>Subjects</th>
                  <th style={th}>Fee</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExams.map((exam, i) => (
                  <tr key={exam.id} style={{ background: i % 2 === 0 ? "#f8fafc" : "white" }}>
                    <td style={td}>{i + 1}</td>
                    <td style={td}><strong>{exam.title}</strong></td>
                    <td style={td}>{exam.total_questions} Qs</td>
                    <td style={td}>{exam.duration_minutes} min</td>
                    <td style={td}>Class {exam.class_id}</td>
                    <td style={td}>{exam.subject_ids?.length || 0} Subjects</td>
                    <td style={td}>₹{exam.entry_fee}</td>

                    <td style={td}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => nav(`/admin/mega-scholarship/${exam.id}/preview`)} style={{ ...btn, background: "#3b82f6" }}>
                          Quiz
                        </button>

                        <button onClick={() => nav(`/admin/mega-scholarship/${exam.id}`)} style={{ ...btn, background: "#3b82f6" }}>
                          View
                        </button>

                        {/* FIXED ROUTE HERE */}
                        <button 
                          onClick={() => nav(`/admin/edit-mega-scholarship/${exam.id}`)} 
                          style={{ ...btn, background: "#f59e0b" }}
                        >
                          Edit
                        </button>

                        <button onClick={() => deleteExam(exam.id)} style={{ ...btn, background: "#ef4444" }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


// Styles
const th = { padding: "18px 16px", textAlign: "left", color: "white", fontWeight: 600, fontSize: 14 };
const td = { padding: "18px 16px", fontSize: 14, color: "#1e293b" };
const btn = { padding: "8px 14px", color: "white", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" };

